// SidePanel — per-side keypoint roster + bulk controls. Spec §6.4.
//
// Two stacked sub-panels:
//   1. Full roster — every keypoint for the current side+frame as a scrollable
//      list. Click a row to select it. The selected row is tinted green if
//      qualified, red if unqualified. Unqualified rows expose an inline lock
//      toggle; qualified rows show a static lock indicator (qualified is
//      implicitly locked — the edits slice refuses writes). When a row is
//      selected and editable (unqualified or already has an edit), an inline
//      strip below the row reveals the Visibility dropdown and Clear button.
//   2. Bulk controls — Undo, Redo, Lock all, Revert frame (with confirm).
//
// Edits override predictions for xy / visibility / locked when present.

import { Lock, LockOpen } from 'lucide-react';
import * as React from 'react';
import { KeypointShapeIcon } from '../../../components/KeypointShapeIcon';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { KEYPOINT_COLORS, KEYPOINT_SHAPES } from '../../../config/constants';
import { KEYPOINTS, visibleKeypoints } from '../../../config/keypoints';
import { t } from '../../../config/strings';
import { cn } from '../../../lib/cn';
import { useStore } from '../../../store';
import type { Side } from '../../../store/predictions';
import type { EditedKeypoint, KeypointName, KeypointRecord } from '../../../types';

export interface SidePanelProps {
  side: Side;
}

function humanize(name: KeypointName): string {
  return t.keypoints[name];
}

function formatCoord(xy: [number, number]): string {
  return `(${xy[0].toFixed(1)}, ${xy[1].toFixed(1)})`;
}

interface Effective {
  xy: [number, number];
  visibility: EditedKeypoint['visibility'];
  locked: boolean;
}

function effectiveFrom(pred: KeypointRecord, edit: EditedKeypoint | undefined): Effective {
  if (edit) {
    return { xy: edit.xy, visibility: edit.visibility, locked: edit.locked };
  }
  return { xy: pred.xy, visibility: pred.defaultVisibility, locked: false };
}

const SELECTED_BG_QUALIFIED = '#dcfce7';
const SELECTED_BG_UNQUALIFIED = '#fee2e2';

export function SidePanel({ side }: SidePanelProps): React.ReactElement {
  return (
    <div className="flex h-full flex-col overflow-hidden" data-testid={`side-panel-${side}`}>
      <AllKeypointsList side={side} />
      <div className="border-t border-border" />
      <BulkControls side={side} />
    </div>
  );
}

// ─── Panel 1 — Roster ───────────────────────────────────────────────────────

function AllKeypointsList({ side }: { side: Side }): React.ReactElement {
  const preds = useStore((s) => {
    if (side === 'front_left') return s.predFrontLeft;
    if (side === 'front_right') return s.predFrontRight;
    if (side === 'left') return s.predLeft;
    return s.predRight;
  });
  const edits = useStore((s) => {
    if (side === 'front_left') return s.editsFrontLeft;
    if (side === 'front_right') return s.editsFrontRight;
    if (side === 'left') return s.editsLeft;
    return s.editsRight;
  });
  const currentFrame = useStore((s) => s.currentFrame);
  const selected = useStore((s) => {
    if (side === 'front_left') return s.selectedFrontLeft;
    if (side === 'front_right') return s.selectedFrontRight;
    if (side === 'left') return s.selectedLeft;
    return s.selectedRight;
  });
  const selectKeypoint = useStore((s) => s.selectKeypoint);
  const setActiveView = useStore((s) => s.setActiveView);
  const setKeypoint = useStore((s) => s.setKeypoint);
  const setVisibility = useStore((s) => s.setVisibility);
  const clearKeypoint = useStore((s) => s.clearKeypoint);

  const frameEdits = edits[currentFrame] ?? {};
  const framePreds = preds?.[currentFrame] ?? [];
  const visible = visibleKeypoints(side);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" data-testid={`roster-${side}`}>
      <div className="sticky top-0 bg-background px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t.sidePanel.allKeypoints}
      </div>
      <ul className="flex flex-col">
        {KEYPOINTS.map(({ name }) => {
          if (!visible.has(name)) return null;
          const pred = framePreds.find((r) => r.name === name);
          if (!pred) return null;
          const edit = frameEdits[name];
          return (
            <RosterRow
              key={name}
              side={side}
              name={name}
              pred={pred}
              edit={edit}
              isSelected={selected === name}
              currentFrame={currentFrame}
              onSelect={() => {
                selectKeypoint(side, name);
                setActiveView(side);
              }}
              setKeypoint={setKeypoint}
              setVisibility={setVisibility}
              clearKeypoint={clearKeypoint}
            />
          );
        })}
      </ul>
    </div>
  );
}

interface RosterRowProps {
  side: Side;
  name: KeypointName;
  pred: KeypointRecord;
  edit: EditedKeypoint | undefined;
  isSelected: boolean;
  currentFrame: number;
  onSelect: () => void;
  setKeypoint: ReturnType<typeof useStore.getState>['setKeypoint'];
  setVisibility: ReturnType<typeof useStore.getState>['setVisibility'];
  clearKeypoint: ReturnType<typeof useStore.getState>['clearKeypoint'];
}

const VISIBILITY_OPTIONS: EditedKeypoint['visibility'][] = ['visible', 'occluded', 'out_of_frame'];

function RosterRow({
  side,
  name,
  pred,
  edit,
  isSelected,
  currentFrame,
  onSelect,
  setKeypoint,
  setVisibility,
  clearKeypoint,
}: RosterRowProps): React.ReactElement {
  const qualified = pred.qualified;
  const eff = effectiveFrom(pred, edit);
  const showInlineEditor = !qualified || !!edit;

  const rowStyle = isSelected
    ? {
        backgroundColor: qualified ? SELECTED_BG_QUALIFIED : SELECTED_BG_UNQUALIFIED,
      }
    : undefined;

  const handleLockToggle = (event: React.MouseEvent): void => {
    event.stopPropagation();
    if (qualified) return;
    setKeypoint(side, currentFrame, name, {
      xy: eff.xy,
      visibility: eff.visibility,
      locked: !eff.locked,
    });
  };

  const handleVisibilityChange = (next: EditedKeypoint['visibility']): void => {
    // Position/restore handling lives in the store action, which also
    // propagates front_left → front_right (visibility only).
    setVisibility(side, currentFrame, name, next);
  };

  const handleClear = (event: React.MouseEvent): void => {
    event.stopPropagation();
    clearKeypoint(side, currentFrame, name);
  };

  return (
    <li
      data-testid={`roster-row-${side}-${name}`}
      onClick={onSelect}
      style={rowStyle}
      className={cn(
        'flex cursor-pointer flex-col gap-1.5 px-3 py-1.5 text-sm leading-tight',
        !isSelected && 'hover:bg-accent/60'
      )}
    >
      <div className="flex items-center gap-2">
        <KeypointShapeIcon
          shape={KEYPOINT_SHAPES[name]}
          color={KEYPOINT_COLORS[name]}
          size={12}
          className="shrink-0"
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-medium">{humanize(name)}</span>
          <span className="truncate text-xs text-muted-foreground">
            {pred.score.toFixed(2)} · {formatCoord(eff.xy)}
          </span>
        </div>
        {qualified ? (
          <span
            data-testid={`lock-${side}-${name}`}
            className="shrink-0 text-muted-foreground"
            title={t.sidePanel.qualifiedLocked}
            aria-label={t.sidePanel.qualifiedLocked}
          >
            <Lock className="h-4 w-4" />
          </span>
        ) : (
          <button
            type="button"
            data-testid={`lock-${side}-${name}`}
            onClick={handleLockToggle}
            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label={eff.locked ? t.sidePanel.unlockKeypoint : t.sidePanel.lockKeypoint}
            aria-pressed={eff.locked}
            title={eff.locked ? t.sidePanel.unlock : t.sidePanel.lock}
          >
            {eff.locked ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
          </button>
        )}
      </div>

      {showInlineEditor && (
        <div className="flex flex-col gap-1.5 pl-5" onClick={(e) => e.stopPropagation()}>
          {!qualified && (
            <div className="flex flex-col gap-1 text-xs">
              <div
                data-testid={`visibility-${side}`}
                role="group"
                className="grid grid-cols-3 gap-1"
              >
                {VISIBILITY_OPTIONS.map((opt) => {
                  const active = eff.visibility === opt;
                  return (
                    <Button
                      key={opt}
                      type="button"
                      size="sm"
                      variant={active ? 'default' : 'outline'}
                      data-testid={`visibility-${side}-${opt}`}
                      aria-pressed={active}
                      disabled={eff.locked}
                      onClick={() => handleVisibilityChange(opt)}
                      className="h-7 w-full border-2 text-xs"
                    >
                      {t.sidePanel.visibilityLabel(opt)}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            data-testid={`clear-${side}`}
            onClick={handleClear}
            disabled={!edit}
            className="self-start"
          >
            {t.sidePanel.clear}
          </Button>
        </div>
      )}
    </li>
  );
}

// ─── Panel 2 — Bulk controls ────────────────────────────────────────────────

function BulkControls({ side }: { side: Side }): React.ReactElement {
  const preds = useStore((s) => {
    if (side === 'front_left') return s.predFrontLeft;
    if (side === 'front_right') return s.predFrontRight;
    if (side === 'left') return s.predLeft;
    return s.predRight;
  });
  const edits = useStore((s) => {
    if (side === 'front_left') return s.editsFrontLeft;
    if (side === 'front_right') return s.editsFrontRight;
    if (side === 'left') return s.editsLeft;
    return s.editsRight;
  });
  const currentFrame = useStore((s) => s.currentFrame);
  const setAllLocked = useStore((s) => s.setAllLocked);
  const resetFrame = useStore((s) => s.resetFrame);
  const undo = useStore((s) => s.undo);
  const redo = useStore((s) => s.redo);
  const pastLen = useStore((s) => s.past.length);
  const futureLen = useStore((s) => s.future.length);

  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const framePreds = preds?.[currentFrame] ?? [];
  const frameEdits = edits[currentFrame] ?? {};

  // "Lock all" is meaningful only when at least one unqualified keypoint is
  // currently unlocked. (Qualified ones are uneditable and are skipped.)
  const canLockAll = framePreds.some((p) => {
    if (p.qualified) return false;
    const e = frameEdits[p.name];
    return !(e?.locked ?? false);
  });
  const hasFrameEdits = Object.keys(frameEdits).length > 0;

  const handleLockAll = (): void => {
    setAllLocked(side, currentFrame, true);
  };
  const handleRevertFrame = (): void => {
    resetFrame(side, currentFrame);
    setConfirmOpen(false);
  };

  return (
    <div className="flex shrink-0 flex-col gap-1.5 p-2">
      <div className="grid grid-cols-2 gap-1.5">
        <Button
          variant="outline"
          size="sm"
          data-testid={`undo-${side}`}
          disabled={pastLen === 0}
          onClick={undo}
          title={t.sidePanel.undoTitle}
        >
          {t.sidePanel.undo}
        </Button>
        <Button
          variant="outline"
          size="sm"
          data-testid={`redo-${side}`}
          disabled={futureLen === 0}
          onClick={redo}
          title={t.sidePanel.redoTitle}
        >
          {t.sidePanel.redo}
        </Button>
        <Button
          variant="outline"
          size="sm"
          data-testid={`lock-all-${side}`}
          disabled={!canLockAll}
          onClick={handleLockAll}
        >
          {t.sidePanel.lockAll}
        </Button>
        <Button
          variant="destructive"
          size="sm"
          data-testid={`revert-frame-${side}`}
          disabled={!hasFrameEdits}
          onClick={() => setConfirmOpen(true)}
          title={t.sidePanel.revertFrameTitle}
        >
          {t.sidePanel.revertFrame}
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.sidePanel.revertDialogTitle}</DialogTitle>
            <DialogDescription>
              {t.sidePanel.revertDesc(t.views[side], currentFrame)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              {t.sidePanel.cancel}
            </Button>
            <Button
              variant="destructive"
              data-testid={`revert-frame-confirm-${side}`}
              onClick={handleRevertFrame}
            >
              {t.sidePanel.revert}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
